/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { ActivityIndicator, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import type { CsvColumnHint, CsvFlowMode } from '../csv-core/domain/csvUiCopy';
import { formatCsvColumnRequirement } from '../csv-core/domain/csvUiCopy';
import { colors } from '../theme/public-api';
import { posSharedUi } from '../../shared/ui/posSharedUi';
import { csvImportExportModalStyles as styles } from './csvImportExportModal.styles';

function ColumnHintsPanel({
  hints,
  expanded,
  onToggle,
}: {
  hints: CsvColumnHint[];
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <View style={styles.hintsPanel}>
      <TouchableOpacity style={styles.hintsToggle} onPress={onToggle} accessibilityRole="button">
        <Text style={styles.hintsToggleLabel}>Columnas del archivo</Text>
        <Ionicons
          name={expanded ? 'chevron-up' : 'chevron-down'}
          size={18}
          color={colors.textSecondary}
        />
      </TouchableOpacity>
      {expanded ? (
        <View style={styles.hintsTable}>
          <View style={styles.hintsHeaderRow}>
            <Text style={[styles.hintsCell, styles.hintsColName]}>Columna</Text>
            <Text style={[styles.hintsCell, styles.hintsColReq]}>Tipo</Text>
          </View>
          {hints.map((h) => (
            <View key={h.column} style={styles.hintsDataRow}>
              <View style={styles.hintsColName}>
                <Text style={[styles.hintsCell, styles.hintsMono]}>{h.column}</Text>
                {h.formatNote ? <Text style={styles.hintsFormatNote}>{h.formatNote}</Text> : null}
              </View>
              <Text style={[styles.hintsCell, styles.hintsColReq]}>
                {formatCsvColumnRequirement(h.requirement)}
              </Text>
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
}

type CsvImportGuideStepProps = {
  guideNote: string;
  mode: CsvFlowMode;
  branchLabel?: string;
  maxProducts?: number | null;
  canEdit: boolean;
  loading: boolean;
  columnsExpanded: boolean;
  columnHints: CsvColumnHint[];
  showTemplate: boolean;
  templateLabel: string;
  exportLabel: string;
  /** Segundo alcance de export (ej. «listado filtrado actual» en Stock). */
  filteredExportLabel?: string;
  onToggleColumnsExpanded: () => void;
  onDownloadTemplate: () => void;
  onDownloadExport: () => void;
  onDownloadFilteredExport?: () => void;
  onPickAndPreview: () => void;
};

export const CsvImportGuideStep = ({
  guideNote,
  mode,
  branchLabel,
  maxProducts,
  canEdit,
  loading,
  columnsExpanded,
  columnHints,
  showTemplate,
  templateLabel,
  exportLabel,
  filteredExportLabel,
  onToggleColumnsExpanded,
  onDownloadTemplate,
  onDownloadExport,
  onDownloadFilteredExport,
  onPickAndPreview,
}: CsvImportGuideStepProps) => (
  <>
    <Text style={styles.guideNote}>{guideNote}</Text>

    {mode === 'stock' && branchLabel ? <Text style={styles.metaLine}>Sucursal: {branchLabel}</Text> : null}
    {maxProducts != null ? (
      <Text style={styles.metaLine}>Hasta {maxProducts} productos activos en tu plan.</Text>
    ) : null}

    <View style={styles.actionRow}>
      {showTemplate ? (
        <TouchableOpacity
          style={[posSharedUi.secondaryButton, styles.actionBtn]}
          onPress={onDownloadTemplate}
          disabled={loading || (mode !== 'stock' && !canEdit)}
        >
          <Ionicons name="document-text-outline" size={16} color={colors.textPrimary} />
          <Text style={styles.actionBtnLabel}>{templateLabel}</Text>
        </TouchableOpacity>
      ) : null}
      <TouchableOpacity
        style={[posSharedUi.secondaryButton, styles.actionBtn]}
        onPress={onDownloadExport}
        disabled={loading}
      >
        <Ionicons name="download-outline" size={16} color={colors.textPrimary} />
        <Text style={styles.actionBtnLabel}>{exportLabel}</Text>
      </TouchableOpacity>
      {filteredExportLabel && onDownloadFilteredExport ? (
        <TouchableOpacity
          style={[posSharedUi.secondaryButton, styles.actionBtn]}
          onPress={onDownloadFilteredExport}
          disabled={loading}
          accessibilityLabel={`Exportar ${filteredExportLabel}`}
        >
          <Ionicons name="download-outline" size={16} color={colors.textPrimary} />
          <Text style={styles.actionBtnLabel}>{filteredExportLabel}</Text>
        </TouchableOpacity>
      ) : null}
    </View>

    {canEdit ? (
      <TouchableOpacity
        style={[posSharedUi.primaryButton, styles.importBtn]}
        onPress={onPickAndPreview}
        disabled={loading}
      >
        {loading ? (
          <ActivityIndicator color={colors.background} />
        ) : (
          <>
            <Ionicons name="document-attach-outline" size={18} color={colors.background} />
            <Text style={posSharedUi.primaryButtonText}>Elegir archivo e importar</Text>
          </>
        )}
      </TouchableOpacity>
    ) : (
      <Text style={styles.readOnlyHint}>Solo lectura: podés exportar, no importar.</Text>
    )}

    <ColumnHintsPanel hints={columnHints} expanded={columnsExpanded} onToggle={onToggleColumnsExpanded} />
  </>
);
